import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Create as CreateIcon,
  CancelOutlined as CancelIcon,
  CheckCircleOutlineOutlined as CheckIcon,
} from '@material-ui/icons';
import {
  Box,
  Divider,
  Typography,
  withStyles,
  Dialog,
} from '@material-ui/core';
import PF2MGenericButton from '~/components/PF2MGenericButton';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import { Card, CardBody } from '~/components/Card';
import PF2MSelectOption from '~/components/PF2MSelectOption';
import styles from './styles';

const Header = ({
  classes,
  data,
  translate,
  selectBatch,
  setSelectBatch,
  options,
  editedLines,
}) => (
  <Box className={classes.boxWrapper}>
    <Box className={classes.boxHeader}>
      <div className={classes.iconButtonHeader}>
        <CreateIcon className={classes.iconHeader} />
      </div>
      <Box>
        <Typography className={classes.titleHeader}>
          {translate('common:BulkEdit')}
          {' '}
          •
          {' '}
          <span className={classes.highlightHeader}>{data?.type}</span>
        </Typography>
        <Typography className={classes.descriptionHeader}>
          {translate('common:TotalOf')}
          {' '}
          <span className={classes.highlightHeader}>{editedLines}</span>
          {' '}
          {translate('common:ChangedResults')}
        </Typography>
      </Box>
    </Box>
    <Divider />
    <Box>
      <Typography className={classes.instructionsHeader}>
        {translate('common:RowRangeToEdit')}
      </Typography>
      <Typography className={classes.alertText}>
        {translate('common:BulkUpdateInstructions')}
      </Typography>
    </Box>
    <PF2MSelectOption
      value={selectBatch}
      options={options}
      onChange={e => setSelectBatch(e.target.value)}
    />
    <Divider />
    <Typography className={classes.instructionsHeader}>
      {translate('common:FieldsForEditing')}
    </Typography>
  </Box>
);

const Actions = ({
  classes,
  handleClose,
  disabledSave,
  handleSave,
  translate,
}) => (
  <Box className={classes.actionsWrapper}>
    <Divider />
    <Box className={classes.actionsButtonsDialog}>
      <PF2MGenericButton
        startIcon={<CancelIcon fontSize="small" />}
        onClick={handleClose}
        variant="outlined"
        color="primary"
      >
        {translate('common:Cancel')}
      </PF2MGenericButton>
      <PF2MGenericButton
        variant={'contained'}
        startIcon={<CheckIcon fontSize="small" color="inherit" />}
        disabled={disabledSave}
        onClick={handleSave}
        color="secondary"
        className={classes.saveButton}
      >
        {translate('common:Save')}
      </PF2MGenericButton>
    </Box>
  </Box>
);

const AlertContent = ({ classes, translate, editedLines }) => (
  <Box className={classes.alertContent}>
    <Typography className={classes.alertText}>
      {translate('common:NumberOfLinesEdited')}
      <span className={classes.highlightHeader}>
        {' '}
        {editedLines || 0}
      </span>
    </Typography>
    <Typography className={classes.alertHighlight}>
      {translate('common:ConfirmContinue')}
    </Typography>
  </Box>
);

const BulkFormModal = ({
  open,
  onClose,
  onSave,
  disableSave,
  data,
  classes,
  children,
  batchOptions,
}) => {
  const { t: translate } = useTranslation();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectBatch, setSelectBatch] = useState('');

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const getSliceRangeFromSelectedBatch = (selectedBatch) => {
    if (!selectedBatch || !selectedBatch.includes('-')) return [0, 0];

    const [start, end] = selectedBatch.split('-');
    const sliceStart = Number(start) - 1;
    const sliceEnd = Number(end);
    return [sliceStart, sliceEnd];
  };

  const handleSaveData = (selectedBatch) => {
    const [sliceStart, sliceEnd] = getSliceRangeFromSelectedBatch(selectedBatch);
    onSave({ sliceStart, sliceEnd });
    setOpenConfirm(false);
  };

  const totalEditedLines = (selectedBatch) => {
    if (!selectedBatch || !selectedBatch.includes('-')) return 0;
    const [start, end] = selectedBatch.split('-');
    const total = Number(end) - Number(start) + 1;
    return total;
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper,
        }}
        scroll="body"
      >
        <Card className={classes.cardMain}>
          <Header
            classes={classes}
            data={data}
            translate={translate}
            selectBatch={selectBatch}
            setSelectBatch={setSelectBatch}
            options={batchOptions}
            editedLines={totalEditedLines(selectBatch)}
          />
          <CardBody className={classes.cardBody}>
            {children}
            <Actions
              classes={classes}
              handleClose={onClose}
              disabledSave={disableSave || !selectBatch}
              handleSave={handleOpenConfirm}
              translate={translate}
            />
          </CardBody>
        </Card>
      </Dialog>
      {openConfirm && (
        <PF2MAlertDialog
          open={openConfirm}
          title={translate('common:BulkUpdate')}
          confirmText={translate('common:Confirm')}
          classesContent={classes.dialogAlertContentCustom}
          classesActions={classes.dialogAlertActionsCustom}
          classesButtons={classes.dialogAlertButtonCustom}
          cancelButtonProps={{
            variant: 'outlined',
            startIcon: <CancelIcon fontSize="small" />,
          }}
          confirmButtonProps={{
            startIcon: <CheckIcon fontSize="small" />,
            disabled: !selectBatch || totalEditedLines(selectBatch) === 0,
            className: classes.saveButton,
          }}
          content={(
            <AlertContent
              translate={translate}
              classes={classes}
              editedLines={totalEditedLines(selectBatch)}
            />
          )}
          onConfirm={() => {
            handleSaveData(selectBatch);
          }}
          onClose={() => setOpenConfirm(false)}
        />
      )}
    </>
  );
};

BulkFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  disableSave: PropTypes.bool,
  data: PropTypes.shape({
    type: PropTypes.string,
  }),
  classes: PropTypes.object,
  children: PropTypes.node.isRequired,
  batchOptions: PropTypes.arrayOf(PropTypes.string),
};

BulkFormModal.defaultProps = {
  onClose: () => {},
  onSave: () => {},
  disableSave: false,
  data: {},
  classes: {},
  batchOptions: [],
};

Header.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  selectBatch: PropTypes.string.isRequired,
  setSelectBatch: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  editedLines: PropTypes.number.isRequired,
};

Actions.propTypes = {
  classes: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  disabledSave: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
};

AlertContent.propTypes = {
  classes: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  editedLines: PropTypes.number.isRequired,
};

export default withStyles(styles)(BulkFormModal);
